/**
 * One outcome capability in one of the three states it can be in during a
 * conversation: decided true, decided false, or not yet known.
 */
import { Outcome } from "@financial-advisory/data-access/api-types";
import { Check, X } from "lucide-react";
import { cn } from "@unlikelyai-magic/utils";

export type OutcomeChipState = "true" | "false" | "undecided";

export const outcomeChipState = (
  outcome: Outcome | null | undefined
): OutcomeChipState => {
  if (outcome === Outcome.TRUE) return "true";
  if (outcome === Outcome.FALSE) return "false";
  return "undecided";
};

interface OutcomeChipProps {
  name: string;
  state: OutcomeChipState;
  onClick?: () => void;
}

export const OutcomeChip = ({ name, state, onClick }: OutcomeChipProps) => {
  const className = cn(
    "inline-flex items-center gap-1.5 rounded-full border-2 px-2.5 py-0.5 text-[13px]",
    state === "true" && "border-green-500 bg-green-500/10 font-medium",
    state === "false" && "border-red-500 bg-brand-primary/[0.08]",
    state === "undecided" &&
      "border-dashed border-gray-300 bg-brand-primary/[0.08] text-brand-muted",
    onClick &&
      "transition-shadow hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-unlikely/40"
  );

  const content = (
    <>
      {state === "true" && (
        <Check
          className="h-[13px] w-[13px] flex-none stroke-[3] text-green-500"
          aria-hidden={true}
        />
      )}
      {state === "false" && (
        <X
          className="h-[13px] w-[13px] flex-none stroke-[3] text-red-500"
          aria-hidden={true}
        />
      )}
      {name}
    </>
  );

  if (!onClick) {
    return <span className={className}>{content}</span>;
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {content}
    </button>
  );
};
